import { some, chain } from 'obliterator';
import { MeshStandardMaterial, Quaternion, Vector3 } from 'three';
import type { Box3, BoxHelper } from 'three';

import ViewPort from '../ViewPort';
import {
  BOUNDBOX_VIEWPORT_FACTOR,
  COLOR_CODES,
  ENTITY_TYPES,
  ORTHOGRAPHIC_CAMERA_DEFAULTS,
  TWEEN_TIMES,
} from '../constants';
import type { PART_TYPES } from '../constants';
import type { VectorJson } from '../contracts/basic';
import { getBoundingBoxDiagonal, isClose } from '../geom-utils';
import type { Body } from '../loaders/body';
import { constructEntityName } from '../loaders/step/utils';
import { boxesUnion, prepareBoundBoxForBodies } from './boundbox-utils';
import {
  ensureCameraFarSettingCompatibleWithMaxDistance,
  getSafePositionToPreventClippingForOrthographicCamera,
} from './control-utils';
import {
  decodeViewerState,
  getEncodedViewerStateAndPropsFromString,
} from './load-utils';
import type { DecodedState } from './load-utils';
import type { Renderer, RendererState } from './renderer';
import {
  makeCameraPositionTween,
  makeCameraPositionTweenWithDeflection,
  makeColorTween,
  makeControlTargetTween,
  makeMaterialColorTween,
  makeOrthoCursorTween,
  makeUpTween,
  type TweenStart,
} from './tween-utils';

const getFinalStateForSnapTo = (
  renderer: Renderer,
  decodedState: DecodedState,
  currentZoomOneViewport: ViewPort,
): {
  finalPosition: Vector3;
  finalTarget: Vector3;
  finalUp: Vector3;
  finalView: ViewPort;
  finalZoom: number;
  finalMaxSceneDistance: number;
} => {
  const {
    cameraPosition,
    cameraUp,
    cameraZoom,
    controlTarget,
    maxBoundingBoxDiagonal,
    nodeHeight,
    nodeWidth,
    orthoView,
  } = decodedState;

  const { bodyOrAssemblyInstanceById, visibleBodies } = renderer.state;

  // establish final conditions
  let finalPosition: Vector3,
    finalTarget: Vector3,
    finalUp: Vector3,
    finalView: ViewPort | undefined,
    finalZoom: number | undefined,
    finalMaxSceneDistance: number | undefined;

  if (cameraPosition) {
    // need to make sure camera is far back enough from selected body to prevent clipping
    const { cameraPosition: finalCamPos, maxDistance } =
      getSafePositionToPreventClippingForOrthographicCamera({
        desiredCameraPosition: cameraPosition.clone(),
        controlTarget: controlTarget || renderer.trackball.target,
        scene: renderer.scene,
      });
    finalPosition = finalCamPos;
    finalMaxSceneDistance = maxDistance;
  } else {
    const { maxDistance } =
      getSafePositionToPreventClippingForOrthographicCamera({
        desiredCameraPosition: renderer.camera.position.clone(),
        controlTarget: controlTarget || renderer.trackball.target,
        scene: renderer.scene,
      });
    finalPosition = renderer.camera.position.clone();
    finalMaxSceneDistance = maxDistance;
  }

  if (controlTarget) {
    finalTarget = new Vector3(
      controlTarget.x,
      controlTarget.y,
      controlTarget.z,
    );
  } else {
    finalTarget = renderer.trackball.target.clone();
  }

  if (cameraUp) {
    finalUp = new Vector3(cameraUp.x, cameraUp.y, cameraUp.z);
  } else {
    finalUp = renderer.camera.up.clone();
  }

  // for comment creation where we store explicit camera zoom value
  if (cameraZoom && orthoView) {
    // need to resolve in case of different screen sizes when comment was made
    // if current width or height is smaller than when comment was made, we have
    // to artifically zoom the image out to capture all the contents
    let zoomResolutionFactor = 1.0;
    if (nodeHeight && nodeWidth) {
      const xOffsetFactor = renderer.nodeWidth / nodeWidth;
      const yOffsetFactor = renderer.nodeHeight / nodeHeight;
      zoomResolutionFactor = Math.min(xOffsetFactor, yOffsetFactor, 1.0);
    }
    // need to resolve against there potentially be a different maximum bounding box
    // diagonal size from current value to one taken at the time of the snapshot
    // i.e. if material stock has been created or if bodies have been added or removed
    let maxDiagonalResolutionZoomFactor = 1.0;
    if (nodeHeight && nodeWidth && maxBoundingBoxDiagonal) {
      const snapShotViewport = ViewPort.getForBoundingBoxDiagonal(
        maxBoundingBoxDiagonal,
        nodeHeight,
        nodeWidth,
        BOUNDBOX_VIEWPORT_FACTOR,
      );
      const snapShotMaxViewportDiagonal = snapShotViewport.diagonal;
      const currentZoomOneViewportDiagonal = currentZoomOneViewport.diagonal;
      maxDiagonalResolutionZoomFactor =
        currentZoomOneViewportDiagonal / snapShotMaxViewportDiagonal;
    } else if (nodeHeight && nodeWidth) {
      // just resolve against the size of the bounding box without materials
      // NOTE: this is for legacy comment snap to support, should not be the case
      const nonMaterialBoundBox = prepareBoundBoxForBodies(
        (function* () {
          for (const id of visibleBodies) {
            const body = bodyOrAssemblyInstanceById.get(id);
            if (body && !body.isMaterial) {
              yield body; //yield non-material body that is loaded and visible
            }
          }
        })(),
      );
      const nonMaterialZoomOneViewport = ViewPort.getForBoundingBox(
        nonMaterialBoundBox.geometry.boundingBox as Box3,
        nodeHeight,
        nodeWidth,
        BOUNDBOX_VIEWPORT_FACTOR,
      );
      const nonMaterialViewportDiagonal = nonMaterialZoomOneViewport.diagonal;
      const currentZoomOneViewportDiagonal = currentZoomOneViewport.diagonal;
      maxDiagonalResolutionZoomFactor =
        currentZoomOneViewportDiagonal / nonMaterialViewportDiagonal;
    }

    finalZoom =
      zoomResolutionFactor * maxDiagonalResolutionZoomFactor * cameraZoom;
    finalView = currentZoomOneViewport.clone();
    finalView.applyZoom(finalZoom);
    const offset = orthoView.offset;
    // NOTE: we do not scale down offset based on screen size difference because viewport location
    // is independent of viewport scale. The zoom gets us large or small, the offset brings us to the
    // proper area, which is independent of screen scale
    finalView.applyOffset(offset);
  }

  if (!finalView) {
    finalView = currentZoomOneViewport;
  }
  if (!finalZoom) finalZoom = renderer.state.zoom;

  // make sure that we do not achieve severe gimbal lock at the end (meaning look vector and up vector are not parallel)
  const lookVector = new Vector3().subVectors(finalTarget, finalPosition);
  const cross = new Vector3().crossVectors(lookVector, finalUp);
  if (isClose(cross.length(), 0)) {
    finalUp.applyQuaternion(
      new Quaternion().setFromAxisAngle(
        new Vector3(1, 1, 1).normalize(),
        Math.PI / 4,
      ),
    );
  }

  return {
    finalPosition,
    finalTarget,
    finalUp,
    finalView,
    finalZoom,
    finalMaxSceneDistance,
  };
};

export type SnappableFeature = {
  faces: number[];
  edges: number[];
  color: number;
  referenceUuid: string;
  cameraPosition: VectorJson;
  controlTarget: VectorJson;
};

/*
Responsible for snapping the viewer to look at geometric features based on view information
provided in the feature item.
First collect the end conditions, and then create and kick off tweens
*/
const snapToFeatureView = async (
  feature: SnappableFeature,
  renderer: Renderer,
): Promise<void> => {
  const {
    bodyIdsByPartFileUuid,
    bodyOrAssemblyInstanceById,
    focusedBodies,
    visibleBodies,
    visibleStockResultKeys,
  } = renderer.state;

  const {
    edges: edgeIndices,
    faces: faceIndices,
    cameraPosition,
    controlTarget,
    referenceUuid: partUuid,
  } = feature;

  const tempFinalPosition = new Vector3(
    cameraPosition.x,
    cameraPosition.y,
    cameraPosition.z,
  );
  const finalTarget = new Vector3(
    controlTarget.x,
    controlTarget.y,
    controlTarget.z,
  );

  // need to transform the targets and locations
  // for any corresponding active and focused bodies
  const relatedBodies = (bodyIdsByPartFileUuid.get(partUuid) || []).map(
    (id) => bodyOrAssemblyInstanceById.get(id) as Body<PART_TYPES>,
  );

  for (const rbdy of relatedBodies) {
    if (
      focusedBodies.includes(rbdy.id as string) &&
      rbdy.group.visible === true
    ) {
      tempFinalPosition.applyMatrix4(rbdy.transform);
      finalTarget.applyMatrix4(rbdy.transform);
      break;
    }
  }

  const { cameraPosition: finalPosition, maxDistance } =
    getSafePositionToPreventClippingForOrthographicCamera({
      desiredCameraPosition: tempFinalPosition,
      controlTarget: finalTarget,
      scene: renderer.scene,
    });
  ensureCameraFarSettingCompatibleWithMaxDistance(maxDistance, renderer.camera);

  const targetVector = new Vector3()
    .subVectors(finalPosition, finalTarget)
    .normalize();

  // align the feature snap to to one of the principle axes
  const axes = [
    new Vector3(0, 0, 1),
    new Vector3(0, 1, 0),
    new Vector3(1, 0, 1),
  ];
  let finalUp = new Vector3(0, 0, 1);
  for (const axis of axes) {
    const across = new Vector3().crossVectors(targetVector, axis);
    if (!isClose(across.lengthSq(), 0)) {
      across.normalize();
      finalUp = new Vector3().crossVectors(across, targetVector).normalize();
    }
  }

  // snap camera zoom/view to encompass faces and edges, or just the body
  // if there are no faces or edges
  // just grab the first corresponding body with part uuid as this is used just for zoom purposes
  const body = relatedBodies[0] as Body<PART_TYPES.STEP>;
  if (!body.isLoaded) {
    return;
  }
  const bodyId = body.id as string;

  // resolve zoom based on camera projection and bounding box
  const objects: Box3[] = [];
  if (faceIndices && body.obj.faces) {
    for (const ind of faceIndices) {
      const newFace = body.obj.faces.get(
        constructEntityName(bodyId, ENTITY_TYPES.FACE, ind),
      );
      if (newFace) {
        objects.push(newFace.boundBox);
      }
    }
  }
  if (edgeIndices && body.obj.edges) {
    for (const ind of edgeIndices) {
      const newEdge = body.obj.edges.get(
        constructEntityName(bodyId, ENTITY_TYPES.EDGE, ind),
      );
      if (newEdge) {
        objects.push(newEdge.boundBox);
      }
    }
  }
  //TODO: Opportunity to reduce iteration by using function*() like in focus-utils. No need to iterate to
  //create groups and to iterate again in prepareBoundBoxForGroups
  //(Need something like hasRenderFocusedBodies from focus-utils)
  const { min, max } = objects.length
    ? boxesUnion(objects)
    : (body.boundBox.geometry.boundingBox as Box3); // just set it to body group itself

  // TODO: might have to project all points in a geometry to get most accurate
  // projection sizes
  const featureDiagonal = new Vector3().subVectors(max, min).length();
  const bodyDiagonal = new Vector3().subVectors(max, min).length();

  let finalZoom = Math.max(
    ORTHOGRAPHIC_CAMERA_DEFAULTS.MIN_ZOOM,
    Math.min(
      ORTHOGRAPHIC_CAMERA_DEFAULTS.MAX_SNAP_ZOOM,
      bodyDiagonal / featureDiagonal,
    ),
  );

  // adjust zoom to be relative to the body diagonal to current max diagonal
  finalZoom *=
    getBoundingBoxDiagonal(
      (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    ) / bodyDiagonal;

  const currentZoomOneViewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  // here we need to fake the zoom by shrinking the viewport size from trackball default
  const finalView = currentZoomOneViewport.clone();
  finalView.applyZoom(finalZoom);

  // collect checkpoint viewport based on all bodies
  const hasVisibleBodiesToUse = some(
    chain(visibleBodies, visibleStockResultKeys),
    (id) => {
      const body = bodyOrAssemblyInstanceById.get(id);
      return body !== undefined && body.isLoaded;
    },
  );
  const checkpointBoundBox = hasVisibleBodiesToUse
    ? prepareBoundBoxForBodies(
        //provide iterable that iterates on demand, rather then iterating and filtering in separate steps
        (function* () {
          for (const id of chain(visibleBodies, visibleStockResultKeys)) {
            const body = bodyOrAssemblyInstanceById.get(id);
            if (body) {
              yield body;
            }
          }
        })(),
      )
    : (renderer.focusedBoundBox as BoxHelper);
  const checkpointViewport = ViewPort.getForBoundingBox(
    checkpointBoundBox.geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  const tweens: TweenStart[] = [];

  // in out tween for zoom
  const orthoTween = makeOrthoCursorTween(
    renderer,
    renderer.state.orthoView,
    finalView,
    checkpointViewport,
    undefined,
    () => renderer.state.onCameraZoomChange(finalZoom),
    TWEEN_TIMES.FEATURE_SNAP_TO / 2,
    TWEEN_TIMES.FEATURE_SNAP_TO / 2,
    true,
  );
  tweens.push(orthoTween);

  const posTween = makeCameraPositionTweenWithDeflection(
    renderer,
    finalPosition,
    finalUp,
    TWEEN_TIMES.FEATURE_SNAP_TO,
  );
  tweens.push(posTween);

  const controlTween = makeControlTargetTween(
    renderer,
    finalTarget,
    TWEEN_TIMES.FEATURE_SNAP_TO,
  );
  if (controlTween) {
    tweens.push(controlTween);
  }

  const upTween = makeUpTween(renderer, finalUp, TWEEN_TIMES.FEATURE_SNAP_TO);
  tweens.push(upTween);

  const colorTween = makeColorTween(
    renderer,
    feature,
    TWEEN_TIMES.FEATURE_SNAP_TO / 2,
    TWEEN_TIMES.FEATURE_SNAP_TO / 2,
  );
  tweens.push(colorTween);

  // begin all tweens
  await Promise.all(tweens.map((tween) => tween.start()));
};

/*
Responsible for restoring stored views taken at different times when making comments and annotations
*/
const snapToStoredView = async (
  decodedState: DecodedState,
  renderer: Renderer,
): Promise<void> => {
  // need to establish animation tweens to first animate back to control initial state
  // and then to the desired state.
  // Need to tween the zoom, position, target, and up vecotr of control and camera to get this to work
  // We can optimize this later for better design patterns if we want
  const tweens: TweenStart[] = [];
  renderer.trackball.enabled = false;
  renderer.canKeyInteract = false;

  // re-establish focus for renderer
  renderer.state.onFocusChange(true);

  const currentZoomOneViewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  const {
    finalPosition,
    finalTarget,
    finalUp,
    finalView,
    finalZoom,
    finalMaxSceneDistance,
  } = getFinalStateForSnapTo(renderer, decodedState, currentZoomOneViewport);

  // first make sure camera wont clip
  ensureCameraFarSettingCompatibleWithMaxDistance(
    finalMaxSceneDistance,
    renderer.camera,
  );

  // collect checkpoint viewport based on all bodies
  const { bodyOrAssemblyInstanceById, visibleBodies, visibleStockResultKeys } =
    renderer.state;

  const hasVisibleBodiesToUse = some(
    chain(visibleBodies, visibleStockResultKeys),
    (id) => {
      const body = bodyOrAssemblyInstanceById.get(id);
      return body !== undefined && body.isLoaded;
    },
  );
  const checkpointBoundBox = hasVisibleBodiesToUse
    ? prepareBoundBoxForBodies(
        //provide iterable that iterates on demand, rather then iterating and filtering in separate steps
        (function* () {
          for (const id of chain(visibleBodies, visibleStockResultKeys)) {
            const body = bodyOrAssemblyInstanceById.get(id);
            if (body) {
              yield body;
            }
          }
        })(),
      )
    : (renderer.focusedBoundBox as BoxHelper);

  const checkpointViewport = ViewPort.getForBoundingBox(
    checkpointBoundBox.geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  // in out tween for zoom
  const orthoTween = makeOrthoCursorTween(
    renderer,
    renderer.state.orthoView,
    finalView,
    checkpointViewport,
    undefined,
    () => renderer.state.onCameraZoomChange(finalZoom),
    TWEEN_TIMES.STORED_VIEW_SNAP_TO / 2,
    TWEEN_TIMES.STORED_VIEW_SNAP_TO / 2,
    true, // force tween to take effect even when no movement in orientation -- this is needed to turn back on trackball controls + trigger renderer updateState from tweening
  );
  tweens.push(orthoTween);

  const posTween = makeCameraPositionTweenWithDeflection(
    renderer,
    finalPosition,
    finalUp,
    TWEEN_TIMES.STORED_VIEW_SNAP_TO,
  );
  tweens.push(posTween);

  const controlTween = makeControlTargetTween(
    renderer,
    finalTarget,
    TWEEN_TIMES.STORED_VIEW_SNAP_TO,
  );
  tweens.push(controlTween);

  const upTween = makeUpTween(
    renderer,
    finalUp,
    TWEEN_TIMES.STORED_VIEW_SNAP_TO,
  );
  tweens.push(upTween);

  const { bodyPoseInformation } = decodedState;
  if (bodyPoseInformation) {
    const { bodyOrAssemblyInstanceById } = renderer.state;

    // adjust body visibility
    for (const bodyId in bodyPoseInformation) {
      const body = bodyOrAssemblyInstanceById.get(bodyId);
      if (body) body.group.visible = bodyPoseInformation[bodyId].visibility;
    }
  }

  // begin all tweens
  await Promise.all(tweens.map((tween) => tween.start()));
};

export const handleForceViewerPoseUpdate = (
  renderer: Renderer,
  nextState: RendererState,
): [boolean, Promise<void>] => {
  if (
    renderer.state.forcedViewerPose !== nextState.forcedViewerPose &&
    nextState.forcedViewerPose
  ) {
    // extract the viewer state from the full viewer pose stringified tag
    let decodedState;
    try {
      const { encodedViewerState } = getEncodedViewerStateAndPropsFromString(
        nextState.forcedViewerPose,
      );
      decodedState = decodeViewerState(encodedViewerState);
    } catch (error) {
      console.warn('Could not decode viewer state!', error);
      // update state so this condition will not trigger next time
      renderer.state = {
        ...renderer.state,
        forcedViewerPose: nextState.forcedViewerPose,
      };
      renderer.state.acknowledgeSnapTo(nextState.forcedViewerPose);
      return [false, Promise.resolve()];
    }

    const snapToPromise = snapToStoredView(decodedState, renderer);

    // update state so this condition will not trigger next time
    renderer.state = {
      ...renderer.state,
      forcedViewerPose: nextState.forcedViewerPose,
    };

    // acknowledge snap to
    renderer.state.acknowledgeSnapTo(nextState.forcedViewerPose);
    return [true, snapToPromise];
  } else {
    return [false, Promise.resolve()];
  }
};

export const handleFeatureSnapToUpdate = (
  renderer: Renderer,
  nextState: RendererState,
): Promise<void> => {
  if (
    renderer.state.featureSnapTo !== nextState.featureSnapTo &&
    nextState.featureSnapTo
  ) {
    return snapToFeatureView(nextState.featureSnapTo, renderer);
  }
  return Promise.resolve();
};

export const snapToCubeNavigationView = async (
  decodedState: DecodedState,
  renderer: Renderer,
  material: MeshStandardMaterial,
): Promise<void> => {
  const tweens: TweenStart[] = [];
  renderer.trackball.enabled = false;
  renderer.canKeyInteract = false;

  // re-establish focus for renderer
  renderer.state.onFocusChange(true);

  const currentZoomOneViewport = ViewPort.getForBoundingBox(
    (renderer.axisAlignedBoundBox as BoxHelper).geometry.boundingBox as Box3,
    renderer.nodeHeight,
    renderer.nodeWidth,
  );

  const { finalPosition, finalUp } = getFinalStateForSnapTo(
    renderer,
    decodedState,
    currentZoomOneViewport,
  );

  const recolorTween = makeMaterialColorTween(
    renderer,
    material,
    COLOR_CODES.CUBE_HOVER_COLOR_RGB,
  );
  const colorTween = makeMaterialColorTween(
    renderer,
    material,
    COLOR_CODES.CUBE_SELECT_COLOR_RGB,
    () =>
      new Promise((resolve) => {
        setTimeout(async () => {
          renderer.requestRenderIfNotRequested();
          await recolorTween.start();
          renderer.requestRenderIfNotRequested();
          resolve();
        }, TWEEN_TIMES.CUBE_RECOLOR);
      }),
    TWEEN_TIMES.CUBE_COLOR,
  );

  tweens.push(colorTween);

  const posTween = makeCameraPositionTween(
    renderer,
    finalPosition,
    TWEEN_TIMES.CUBE_VIEW_SNAP_TO,
  );
  tweens.push(posTween);

  const upTween = makeUpTween(renderer, finalUp, TWEEN_TIMES.CUBE_VIEW_SNAP_TO);
  tweens.push(upTween);

  // begin all tweens
  await Promise.all(tweens.map((tween) => tween.start()));
};
